import ImageGallery from 'react-image-gallery';
import Skeleton from 'react-loading-skeleton';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  WhiteArrowBackIcon as arrowBack,
  WhiteArrowNextIcon as arrowNext,
  EditIcon as editIcon,
} from '@/shared/ui';
import EditStripModal from './editStripModal';
import useStripImageViewer from './useStripImageViewer';

export type StripImageViewerProps = {
  activeButton: any;
  ecgDataMap: any;
  reportData?: any;
  timezoneOffset?: any;
};

/**
 * Displays the captured strip images for the selected AI event and opens the edit dialog for them. useStripImageViewer supplies the image list and selection; a skeleton covers strip loading, a gallery with custom arrows shows the images and reports load errors through its own overlay, an unavailable-strip message stands in when there are none, and the edit button is disabled while the event is removed or a save is in flight.
 *
 * @param props - Viewer props carrying the report data, ECG data map, timezone offset and the active action button.
 * @returns The rendered strip image gallery with its edit button and, when open, the edit-strip dialog.
 */
const StripImageViewer = ({
  activeButton,
  ecgDataMap,
  reportData,
  timezoneOffset,
}: StripImageViewerProps) => {
  const {
    isLoadingStrip,
    isSavingStripManagement,
    state,
    setState,
    listImages,
    imageRef,
    viewSelectedStrip,
    isDeviceEvent,
    onClickEditStrip,
    closeEditStripModal,
    updateEditData,
  } = useStripImageViewer({ activeButton });

  return (
    <div className="strip-management-ai-strip-content">
      {isLoadingStrip ? (
        <div className="strip-management-ai-strip-content-skeleton">
          <Skeleton height="100%" />
        </div>
      ) : (
        <>
          <div className="ecg-strip">
            {state.stripImgs?.length ? (
              <div className="strip-limit-width">
                <ImageGallery
                  ref={imageRef}
                  items={listImages}
                  infinite={false}
                  showPlayButton={false}
                  showFullscreenButton={false}
                  showBullets
                  lazyLoad
                  disableKeyDown
                  slideDuration={250}
                  startIndex={0}
                  thumbnailPosition="bottom"
                  renderLeftNav={(onClick, disabled) => (
                    <button
                      id="strips-ai-gallery-prev-btn"
                      className="image-gallery-custom-nav left"
                      disabled={disabled}
                      onClick={onClick}
                    >
                      <img src={arrowBack} alt="back" />
                    </button>
                  )}
                  renderRightNav={(onClick, disabled) => (
                    <button
                      id="strips-ai-gallery-next-btn"
                      className="image-gallery-custom-nav right"
                      disabled={disabled}
                      onClick={onClick}
                    >
                      <img src={arrowNext} alt="next" />
                    </button>
                  )}
                  onImageLoad={() => {
                    setState({ isLoading: false, isLoadedImageError: false });
                  }}
                  onImageError={(event) => {
                    if (event.type === 'error') {
                      setState({ isLoading: false, isLoadedImageError: true });
                    }
                  }}
                />
                {state.isLoading ? (
                  <div className="loading-thumbnail">
                    <Skeleton height="101%" />
                  </div>
                ) : state.isLoadedImageError ? (
                  <div className="loading-thumbnail --error">
                    There is an error while loading ECG strips
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="no-ecg-strip-container">
                {/* <img className="no-ecg-strip-icon" src={noDataIcon} alt={'No data icon'} /> */}
                <div className="no-ecg-strip-text">The strip image is currently unavailable</div>
              </div>
            )}
          </div>
          {viewSelectedStrip?.id && (
            <div className="edit-ecg-strip-button-wrapper" id="edit-strip-btn-id">
              <IconButton
                id="auto-event-edit-strip-btn"
                className="edit-ecg-strip-button"
                isFilledGradient
                ariaLabel="Edit strip"
                disabled={viewSelectedStrip?.isRemoved || isLoadingStrip || isSavingStripManagement}
                iconComponent={<img src={editIcon} alt="" />}
                onClick={(e) => {
                  e.preventDefault();
                  onClickEditStrip();
                }}
              />
              <UncontrolledTooltip
                className="custom-tooltip"
                target="edit-strip-btn-id"
                placement="bottom"
                delay={{ show: 0, hide: 0 }}
                hideArrow
              >
                Edit strip
              </UncontrolledTooltip>
            </div>
          )}
        </>
      )}
      {state.isVisibleEditAiStripModal && (
        <EditStripModal
          visible={state.isVisibleEditAiStripModal}
          handleClickCloseButton={() => {
            closeEditStripModal();
          }}
          data={state.selectedData}
          originalData={state.originalSelectedData}
          updateEditData={updateEditData}
          ecgDataMap={ecgDataMap}
          timezoneOffset={timezoneOffset}
          ecgViewerConfig={reportData?.holterProfile?.reportConfiguration?.ecgViewer}
          isDeviceEvent={isDeviceEvent}
        />
      )}
    </div>
  );
};

export default StripImageViewer;
